// @ts-strict-ignore
import React, { useEffect, useRef, useState } from 'react'
import {
	ScreenSection,
	SCREEN_SECTION_TYPE_AUDIO,
	ScreenSectionItem,
	SCREEN_SECTION_TYPE_IMAGE,
	SCREEN_SECTION_TYPE_VIDEO,
	SCREEN_SECTION_TYPE_WEB_VIDEO,
	SCREEN_SECTION_TYPE_AR,
	SCREEN_SECTION_TYPE_ANIMATION,
	Collection,
	Screen,
} from 'mytours-read-common'
import classNames from 'classnames'

import SectionMediaAudioComponent from './SectionMediaAudio'
import SectionMediaVideoComponent from './SectionMediaVideo'
import { getDirMode, waitForSomething } from '../../../platform'
import SectionMediaImage from './SectionMediaImage'
import Slider from './Slider'
import Modal from '../../../common/components/modal'
import { ScreenSize, ScreenSizeBreakpoints } from 'common_lib/types'
import { getScreenSize } from 'common_lib/utils'
import FullscreenMedia from './FullscreenMedia'
import { EventActionsEnum, sendEvent } from 'mobile_web/modules/logging/analytics'
import { getImageAlt, getPageContainer } from 'mobile_web/modules/common/functions'
import { Button, Text, Grid, GridRow } from '@mytours/components'
import '../styles/sectionMediaGroup.less'
import { GetScreensShowResponseSectionItems } from 'mytours-read-api-typescript'
import { getSectionsQueryParams } from 'mobile_web/modules/navigation/queryParams'
import { getImageHeight } from './functions'
import strings from '../../../../strings'
import { formatNumberStyle } from '../../../platform'

export interface Props {
	section: ScreenSection
	collection: Collection
	screen: Screen
}

const SectionMediaGroup: React.FC<Props> = ({ section, collection, screen }) => {
	const [activeSlideIndex, setActiveSlideIndex] = useState<number>(0)
	const [screenSize, setScreenSize] = useState<ScreenSize>(getScreenSize())
	const [imageHeight, setImageHeight] = useState<number>(getImageHeight(screenSize))
	const [showMediaModal, setShowMediaModal] = useState<boolean>(false)
	const [singleModalItem, setSingleModalItem] = useState<ScreenSectionItem>(null)
	const [canWrapSwiper, setCanWrapSwiper] = useState<boolean>(false)
	const [modalPresented, setModalPresented] = useState<boolean>(false)
	const [, setSwiperWrapperElement] = useState<any>()

	const slidesRef = useRef<any>()

	const setImageDimension = () => {
		const newScreenSize = getScreenSize()
		if (screenSize !== newScreenSize) {
			setImageHeight(getImageHeight(screenSize))
			setScreenSize(newScreenSize)
		}
	}

	const updateCanWrapSwiper = () => {
		if (!slidesRef?.current) {
			return
		}

		const sliderStyle = getComputedStyle(slidesRef.current)
		const sliderWidth =
			slidesRef.current.scrollWidth + parseInt(sliderStyle.paddingLeft) + parseInt(sliderStyle.paddingRight)
		const maxWidth = getPageContainer(slidesRef.current).clientWidth * 0.75

		let canWrap
		if (window.innerWidth >= ScreenSizeBreakpoints.md) {
			if (sliderWidth <= maxWidth && !canWrapSwiper) {
				canWrap = true
			} else if (sliderWidth > maxWidth && canWrapSwiper) {
				canWrap = false
			}
		} else if (!canWrapSwiper) {
			canWrap = true
		}

		if (canWrap !== undefined) {
			setCanWrapSwiper(canWrap)
		}
	}

	useEffect(() => {
		const { storySectionId, sectionItemId } = getSectionsQueryParams()

		if (!storySectionId || !sectionItemId) return

		const sectionItem: GetScreensShowResponseSectionItems | undefined = section.section_items.find(
			item => item.id === sectionItemId,
		)

		if (!sectionItem || section.id !== storySectionId) return

		switch (sectionItem.media_type) {
			case SCREEN_SECTION_TYPE_AUDIO:
				// the logic for that case inside AudioPlayerBar.tsx and SectionMediaAudio.tsx components
				break
			case SCREEN_SECTION_TYPE_VIDEO:
			case SCREEN_SECTION_TYPE_WEB_VIDEO:
			case SCREEN_SECTION_TYPE_IMAGE:
				waitForSomething(() => document.getElementById(`section-item-id-${sectionItemId}`)).then(() => {
					setTimeout(() => {
						document.getElementById(`section-item-id-${sectionItemId}`).click()
					}, 1000)
				})
				break
			default:
				break
		}
	}, [])

	useEffect(() => {
		setImageDimension()
		addEventListener('resize', setImageDimension)
		addEventListener('resize', updateCanWrapSwiper)

		return () => {
			removeEventListener('resize', setImageDimension)
			removeEventListener('resize', updateCanWrapSwiper)
		}
	}, [])

	const onModalDidPresent = (): void => setModalPresented(true)

	const onModalDidDismiss = (): void => {
		setShowMediaModal(false)
		setModalPresented(false)
	}

	const renderHeaderComponent = (): JSX.Element => {
		const { title } = section

		return (
			<GridRow dir={getDirMode()} className="block-header header-row">
				{title && (
					<h2 className="subheading">
						<Text variant="inherit" data-cy="story-screen-section-heading">
							{title}
						</Text>
					</h2>
				)}
			</GridRow>
		)
	}

	const toggleFullscreenMediaModal = (singleItem?: ScreenSectionItem, index?: number): void => {
		if (!showMediaModal) {
			sendEvent(EventActionsEnum.OpenLightbox, {
				collection: collection?.name,
				collectionId: collection?.id,
				itemName: screen.name,
			})
		}

		setShowMediaModal(!showMediaModal)
		setSingleModalItem(singleItem || null)
		setActiveSlideIndex(index ?? activeSlideIndex)
	}

	const renderItem = ({
		sectionItem,
		index,
		isSingleItem,
		bigAudioStyle,
		slideshowData = {},
	}: {
		sectionItem: ScreenSectionItem
		index: number
		isSingleItem: boolean
		bigAudioStyle: boolean
		slideshowData?: any
	}): JSX.Element => {
		let sectionItemComponent: JSX.Element

		const { id: sectionId, name, slideshow_enabled: slideshowEnabled } = section

		switch (sectionItem.media_type) {
			case SCREEN_SECTION_TYPE_AUDIO:
				// pick between normal and big audio component
				if (bigAudioStyle) {
					sectionItemComponent = (
						<SectionMediaAudioComponent
							collectionId={collection?.id}
							container={{ type: 'section', id: sectionId, title: name }}
							audioPreview={sectionItem}
							height={imageHeight}
							screen={screen}
						/>
					)
				} else {
					sectionItemComponent = (
						<SectionMediaAudioComponent
							collectionId={collection?.id}
							container={{ type: 'section', id: sectionId, title: name }}
							audioPreview={sectionItem}
							fullScreenEnabled={slideshowData.fullScreenEnabled}
							coverImages={slideshowData.coverImages}
							startTimes={slideshowData.startTimes}
							screen={screen}
							slideshowEnabled={slideshowEnabled}
						/>
					)
				}
				break
			case SCREEN_SECTION_TYPE_VIDEO:
			case SCREEN_SECTION_TYPE_WEB_VIDEO:
				sectionItemComponent = (
					<GridRow className={!isSingleItem ? 'video-row-slider' : 'full-width'}>
						<SectionMediaVideoComponent
							onPlay={isSingleItem ? null : () => onSlideClick(index)}
							sectionItem={sectionItem}
							height={imageHeight}
							isSingleItem={isSingleItem}
							sectionId={section.id}
							sectionTitle={section.name}
							containerName={screen.name}
						/>
					</GridRow>
				)
				break
			case SCREEN_SECTION_TYPE_IMAGE:
			case SCREEN_SECTION_TYPE_ANIMATION:
				sectionItemComponent = (
					<div
						className={!isSingleItem ? 'image-row' : 'full-width'}
						onClick={() => (isSingleItem ? toggleFullscreenMediaModal(sectionItem) : {})}
						role="button"
						data-cy="section-media-group-button"
					>
						<SectionMediaImage
							file={sectionItem.file}
							alt={getImageAlt(sectionItem)}
							height={isSingleItem ? undefined : imageHeight}
						/>
					</div>
				)
				break
			case SCREEN_SECTION_TYPE_AR:
				const thumbnailMediaItem = sectionItem?.thumbnail_media_item ?? sectionItem
				const thumbnailFile = thumbnailMediaItem?.file
				sectionItemComponent = (
					<div
						className={classNames(`${!isSingleItem ? 'image-row' : 'full-width'}`, { 'audio-big': !thumbnailFile })}
						onClick={() => (isSingleItem ? toggleFullscreenMediaModal(sectionItem) : {})}
						role="button"
					>
						{thumbnailFile ? (
							<SectionMediaImage
								file={thumbnailFile}
								alt={getImageAlt(thumbnailMediaItem)}
								height={isSingleItem ? undefined : imageHeight}
							/>
						) : (
							<GridRow
								className="cover-image"
								style={{
									width: formatNumberStyle(imageHeight) || '100%',
									height: formatNumberStyle(imageHeight),
								}}
							>
								<Button
									variant="clear"
									onClick={() => toggleFullscreenMediaModal(sectionItem)}
									aria-label={strings.play_media}
									className="controls controls-small media-action-click"
								>
									<i className="fal fa-head-side-goggles" />
								</Button>
							</GridRow>
						)}
					</div>
				)
				break
			default:
				console.warn(`sectionItem Type : ${sectionItem.media_type}`)
				sectionItemComponent = <div />
				break
		}

		return <React.Fragment key={sectionItem.id}>{sectionItemComponent}</React.Fragment>
	}

	const renderCarouselItem = (sectionItem: ScreenSectionItem, index: number): JSX.Element => (
		<Grid id={`section-item-id-${sectionItem.id}`} data-cy="section-media-group-item">
			{renderItem({ sectionItem, index, isSingleItem: false, bigAudioStyle: true })}
		</Grid>
	)

	/**
	 * Need a way to determine whether the media group should be rendered in vertical or horizontal list
	 */
	const shouldRenderVertical = (): boolean => {
		const { section_items: sectionItems } = section
		// currently only rendering vertical style if ALL items are AUDIO type
		if (sectionItems) {
			const containsNonAudioItem = sectionItems.find(item => item.media_type !== SCREEN_SECTION_TYPE_AUDIO)
			return containsNonAudioItem ? false : true
		}
		return false
	}

	const renderVerticalList = (
		sectionItems: ScreenSectionItem[],
		{ slideshowEnabled, startTimes, fullScreenEnabled },
	): JSX.Element => {
		const coverImages = []

		if (slideshowEnabled) {
			sectionItems.forEach(item => {
				if (item.media_type !== SCREEN_SECTION_TYPE_AUDIO) {
					coverImages.push(item)
				}
			})
			sectionItems = sectionItems.filter(item => item.media_type === SCREEN_SECTION_TYPE_AUDIO)
		}

		const views: JSX.Element[] = sectionItems.map((item, index) =>
			renderItem({
				sectionItem: item,
				index,
				isSingleItem: false,
				bigAudioStyle: false,
				slideshowData: {
					fullScreenEnabled,
					coverImages,
					startTimes,
				},
			}),
		)

		return (
			<Grid className="block-content">
				<Grid className="vertical-audio">{views}</Grid>
			</Grid>
		)
	}

	const onSlideClick = (newActiveSlideIndex: number): void => {
		setActiveSlideIndex(newActiveSlideIndex)
		toggleFullscreenMediaModal(null, newActiveSlideIndex)
	}

	const renderFullscreenMediaModal = (): JSX.Element => {
		const { section_items: sectionItems } = section

		return (
			<Modal
				isOpen={showMediaModal}
				cssClass="media-modal"
				showBackdrop={true}
				onDidDismiss={onModalDidDismiss}
				onDidPresent={onModalDidPresent}
			>
				{modalPresented && (
					<FullscreenMedia
						collectionId={collection?.id}
						mediaData={singleModalItem ? [singleModalItem] : sectionItems}
						activeSlideIndex={singleModalItem ? 0 : activeSlideIndex}
						onBack={() => toggleFullscreenMediaModal()}
						section={section}
						screen={screen}
					/>
				)}
			</Modal>
		)
	}

	const {
		section_items: sectionItems,
		slideshow_enabled: slideshowEnabled,
		start_times: startTimes,
		full_screen: fullScreenEnabled,
	} = section

	const showList = sectionItems && sectionItems.length > 1
	const allAudio = shouldRenderVertical() // contains ALL audio types
	const firstItem = sectionItems && sectionItems.length > 0 ? sectionItems[0] : null

	const canWrap = !showList || canWrapSwiper || allAudio || slideshowEnabled

	// TODO: I've removed this section, because looks like it's already fixed in the backend
	// const isAudioItem =
	// 	sectionItems.length > 0 && sectionItems.some(item => item.media_type === SCREEN_SECTION_TYPE_AUDIO)
	// if (isAudioItem && slideshowEnabled) {
	// 	const audioFileIndex = sectionItems.findIndex(item => item.media_type === SCREEN_SECTION_TYPE_AUDIO)
	// 	// remove audio item from times list
	// 	startTimes.splice(audioFileIndex, 1)
	// }

	// - allow same video to be played in each page without all instantly playing (same file name)
	// - stop playing video on other pages when new one is started (shared state)
	// - shared audio Sound object

	return (
		<Grid className={classNames('media-group-grid', { 'can-wrap': canWrap })}>
			{/* if we have a title display it */}
			{renderHeaderComponent()}

			{allAudio || slideshowEnabled ? (
				renderVerticalList(sectionItems, { slideshowEnabled, startTimes, fullScreenEnabled })
			) : showList ? (
				<GridRow className="block-content slider-block-content" data-cy="story-screen-section-content">
					<Slider
						ref={slidesRef}
						className="media-group-slider-wrapper"
						sliderClassName="media-group-slider"
						renderItem={renderCarouselItem}
						data={sectionItems}
						slideOptions={{
							slidesPerView: 'auto',
							shortSwipes: true,
							watchOverflow: true,
						}}
						onSlideClick={onSlideClick}
						swiperWrapperElement={setSwiperWrapperElement}
					/>
				</GridRow>
			) : firstItem ? (
				// only add the horizontal padding for Non-Audio types (audio should be flush with edge of screen)
				<GridRow className="block-content full-width" data-cy="story-screen-section-content">
					{renderItem({ sectionItem: firstItem, index: 0, isSingleItem: true, bigAudioStyle: false })}
				</GridRow>
			) : null}
			{showMediaModal && renderFullscreenMediaModal()}
		</Grid>
	)
}

export default SectionMediaGroup
